import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { trackPageView } from '../utils/analytics';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiMail, FiClock, FiArrowLeft, FiFileText, FiUsers } = FiIcons;

const FreeLandingPageRUThankYou = () => {
  useEffect(() => {
    trackPageView('Free Landing Page RU Thank You');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Back to home link */}
      <Link to="/" className="fixed top-4 left-4 z-50 bg-black-900/80 p-2 rounded-full hover:bg-black-900 transition-colors">
        <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-white" />
      </Link>

      {/* Confetti Effect */}
      <div className="fixed inset-0 pointer-events-none z-10">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-red-400 to-blue-500 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: -10,
              rotate: 0,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              y: window.innerHeight + 10,
              rotate: 360,
              scale: 0
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              delay: Math.random() * 2,
              ease: "easeOut"
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-red-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-24 h-24 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <SafeIcon icon={FiCheck} className="w-12 h-12 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold text-black-900 mb-6 font-serif"
          >
            Заявка Получена! 🇷🇺
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-black-700 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Спасибо за подачу заявки на бесплатный лендинг! Мы получили вашу информацию и рады создать что-то потрясающее для вашего бизнеса.
          </motion.p>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-black-900 mb-4 font-serif">Что происходит дальше?</h2>
            <div className="w-16 h-1 bg-black-900 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Подтверждение по Email</h3>
              <p className="text-black-700">Вы получите подтверждение по электронной почте в ближайшие несколько минут.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiFileText} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Анкета Бренда</h3>
              <p className="text-black-700">Заполните анкету о вашем бренде, чтобы помочь нам создать идеальную страницу.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Персональное Обращение</h3>
              <p className="text-black-700">Мы свяжемся с вами в течение 48 рабочих часов для обсуждения деталей проекта.</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="bg-black-900 text-white p-8 rounded-lg text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Сроки: 5-7 Рабочих Дней</h3>
            <p className="text-lg text-black-200 mb-6">
              Ваш профессиональный, адаптивный лендинг будет готов в течение 5-7 рабочих дней после получения всех необходимых материалов и согласования направления.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/free-landing-page-ru">
                <button className="px-6 py-3 bg-white text-black-900 rounded-lg font-semibold hover:bg-black-100 transition-colors">
                  Узнать Больше О Нашем Процессе
                </button>
              </Link>
              <Link to="/">
                <button className="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-black-900 transition-colors">
                  Вернуться На Главную
                </button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            className="mt-8 text-center"
          >
            <div className="flex items-center justify-center space-x-2 text-black-600">
              <SafeIcon icon={FiClock} className="w-5 h-5" />
              <span>Ожидаемое время ответа: 48 рабочих часов или менее</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FreeLandingPageRUThankYou;